import {
  BUILT_IN_CATEGORIES,
  readProjectCategory,
  type CustomCategoryDTO,
  type ProjectCategoryRef,
} from '@astral/shared';
import { useT, type TranslationKey } from '../providers/I18nProvider.js';

export interface CategoryPickerProps {
  /** The project's stored columns, as returned by the API. */
  category: string | null;
  customCategoryId: string | null;
  custom: CustomCategoryDTO[];
  onChange: (ref: ProjectCategoryRef) => void;
}

/**
 * Choose a project's category: none, a built-in, or one of the user's own.
 *
 * Shared by Create and the Editor so the two can never drift. The chip row is
 * the existing `.chips` pattern — no new visual language.
 *
 * A LEGACY value (a retired built-in like `other`, or anything unrecognised)
 * shows as "No category" and selects nothing. It is deliberately not offered as
 * a chip and is never written back: the stored value survives untouched until
 * the user explicitly picks something else.
 */
export function CategoryPicker({
  category,
  customCategoryId,
  custom,
  onChange,
}: CategoryPickerProps): JSX.Element {
  const t = useT();
  const current = readProjectCategory({ category, customCategoryId });

  const isActive = (ref: ProjectCategoryRef): boolean => {
    if (ref.kind === 'none') return current.kind === 'none' || current.kind === 'legacy';
    if (ref.kind === 'builtin') return current.kind === 'builtin' && current.id === ref.id;
    if (ref.kind === 'custom') return current.kind === 'custom' && current.id === ref.id;
    return false;
  };

  const chip = (ref: ProjectCategoryRef, label: string) => (
    <button
      key={`${ref.kind}:${'id' in ref ? ref.id : 'none'}`}
      type="button"
      className={`chip${isActive(ref) ? ' chip--active' : ''}`}
      onClick={() => onChange(ref)}
    >
      {label}
    </button>
  );

  return (
    <div className="chips">
      {/* Clearing is always available, and is where a legacy project sits. */}
      {chip({ kind: 'none' }, t('categoryManager.none'))}
      {BUILT_IN_CATEGORIES.map((id) =>
        chip({ kind: 'builtin', id }, t(`categories.${id}` as TranslationKey)),
      )}
      {/* Custom names are the user's own text — never translated. */}
      {custom.map((entry) => chip({ kind: 'custom', id: entry.id }, entry.name))}
    </div>
  );
}
